import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { InviteToMeetingModule } from '@app/account/shared/invite-to-meeting/invite-to-meeting.module';
import { PaymentCheckoutWizardModule } from '@app/account/shared/payment-checkout-wizard/payment-checkout-wizard.module';
import { ChatService } from '@app/core/services/chat.service';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { BreakContractReasonData, classBreakReasonsData } from '@app/library/panes/my-classes-classrooms/constants';
import { ContentRouter } from '@app/models/content-router';

import { CountdownModule } from '@app/shared';
import { loadMeeting } from '@app/store/actions/content.actions';
import { AlertModule } from '@core/components/alert/alert.module';
import { MeetingFormWrapperModule } from '@core/components/meeting-form-wrapper/meeting-form-wrapper.module';
import { MeetingsTitleModule } from '@core/components/meeting-title/meetings-title.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { logger } from '@core/helpers/logger';
import {
  AccountService,
  FeaturesService,
  FilesService,
  MeetingsBffService,
  MeetingsService,
  RequestService,
  UserServiceContractsService,
  UserServicesService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';

import { captureMessage } from '@sentry/angular';
import { MeetingCheckoutWizardDialogModule } from '@shared/checkout-wizards/meeting-checkout-wizard-dialog';
import { UserServiceContractCheckoutDialogModule } from '@shared/checkout-wizards/user-service-contract-checkout-dialog';
import { ComplaintMeetingFormComponent } from '@shared/complaint-meeting-form/complaint-meeting-form.component';
import { formatDateByTimeZone } from '@shared/utils';
import {
  acceptMeeting,
  acceptMeetingReschedule,
  declineMeeting,
  openMeetingRescheduleWindow,
  rejectMeetingReschedule,
  withdrawMeetingReschedule,
} from '@store/actions/profile.actions';
import { getSubjects } from '@store/reducers/data.reducer';
import { differenceInMinutes, differenceInSeconds, format } from 'date-fns';
import {
  BreakUserServiceContractRequest,
  ConfirmationOptions,
  EDashboardMeetingGroup,
  EMeetingUserAction,
  EMeetingUserState,
  EUserServiceType,
  ImageSizeEnum,
  MeetingParticipant,
  MeetingRoleEnum,
  TenantEnum,
  User,
  UserService,
  UserServiceContract,
  UserServiceContractActionEnum,
  UserServiceContractStateEnum,
  UserServiceContractStatusEnum,
  type Meeting,
  MeetingStatusEnum,
} from 'lingo2-models';
import { MeetingTypeEnum } from 'lingo2-models/dist/content/meeting/enums/meeting-type-enum';
import { DateFnsModule } from 'lingo2-ngx-date-fns';
import { ClipboardModule } from 'ngx-clipboard';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiCardMenuModule, OnUiCoverModule } from 'onclass-ui';
import { OnUiCover } from 'onclass-ui/lib/modules/on-ui-cover/on-ui-cover.component';
import { of, tap } from 'rxjs';
import { first, switchMap, takeUntil } from 'rxjs/operators';

interface IMeetingCardComponentState {
  copied: boolean;
  cut: boolean;
  subscribed: boolean;
  inviteModal: boolean;
}

type ConfirmationActionType = 'cancel' | 'leave';

interface IMeetingCardComponentState {
  copied: boolean;
  cut: boolean;
  subscribed: boolean;
  inviteModal: boolean;
  meetingFormModal: boolean;
  complaintForm: boolean;
  confirmationAlert: boolean;
  confirmationId: string;
  confirmationAction: ConfirmationActionType;
  confirmationOptions: ConfirmationOptions;
}

@Component({
  selector: 'app-schedule-mini',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    DateFnsModule,
    OnUiCoverModule,
    OnUiButtonModule,

    CountdownModule,
    OnUiCardMenuModule,
    ClipboardModule,
    ModalModule,
    InviteToMeetingModule,
    PaymentCheckoutWizardModule,
    MobileModalModule,
    MeetingFormWrapperModule,
    AlertModule,
    SpinnersModule,
    MeetingsTitleModule,
    UserServiceContractCheckoutDialogModule,
    MeetingCheckoutWizardDialogModule,
    ComplaintMeetingFormComponent,
    RouterModule,
  ],
  templateUrl: './schedule-mini.component.html',
  styleUrls: ['./schedule-mini.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleMiniComponent extends ChangableComponent {
  private accountsService = inject(AccountService);
  private router = inject(Router);
  private request = inject(RequestService);
  private readonly store = inject(Store);
  deviceService = inject(DeviceDetectorService);
  userServices = inject(UserServicesService);
  chatService = inject(ChatService);
  private contractsService = inject(UserServiceContractsService);
  private meetingsService = inject(MeetingsService);
  private meetingsBffService = inject(MeetingsBffService);
  private features = inject(FeaturesService);

  public meetingUrl: string;
  public state: IMeetingCardComponentState = {
    copied: false,
    cut: false,
    subscribed: false,
    inviteModal: false,
    meetingFormModal: false,
    complaintForm: false,
    confirmationAlert: false,
    confirmationId: null,
    confirmationAction: null,
    confirmationOptions: null,
  };
  public author: User;
  public student: User;
  public meetingRoute: string[];
  public meetingJoinRoute: string[];
  public meetingSubject: string;
  public cover: OnUiCover;
  public avatar: OnUiCover;
  public processing = false;
  public crossHourString = '';
  public hasCrossEvents = false;
  public declineRescheduleAlert = false;
  public meetingCheckoutDialogOpened = false;
  public acceptAndPayReason = 'dashboard.unconfirmed-causes.received-invite-teacher';
  public rescheduleBeginDate: Date;
  public rescheduleEndDate: Date;
  public contractReservationExpireDate: Date;
  public meetingReservationExpireDate: Date;
  public serviceCheckoutDialogOpened = false;

  public getUserFullName = AccountService.getUserFullName;
  public profileUrl = AccountService.accountRoute;

  public classBreakAlertShow = false;
  public classBreakReasons: 'no_classes' | 'left_classes' | 'less_24_hours' = 'no_classes';
  public alertData: BreakContractReasonData = null;

  private _meeting: Meeting;
  private acceptMeetingIntervalLock = false;
  private participant: Partial<MeetingParticipant>;

  @Input() display_mode: string;

  @Input() set meeting(meeting: Meeting) {
    this._meeting = meeting;
    this.store.dispatch(loadMeeting({ meeting_id: meeting.id }));
    this.initCard();
  }

  public get meeting(): Meeting {
    return this._meeting;
  }

  @Input() me: User;

  @Input() set crossHour(date: Date) {
    if (!date) {
      date = new Date();
    }
    this.crossHourString = format(date, 'HH:mm');
    this.hasCrossEvents = true;
  }

  @Input() status: EDashboardMeetingGroup;
  @Output() clicked = new EventEmitter<{ type: string; meeting: Meeting }>();
  @Output() changed = new EventEmitter<boolean>();

  public isMe(): boolean {
    return this.me?.id === this.meeting?.author_id;
  }

  public get contract(): UserServiceContract {
    return this.meeting?.user_service_contract;
  }

  public get paidInProcess(): boolean {
    return (
      this.meeting.status === MeetingStatusEnum.accept_required &&
      this.meeting?.user_service_contract?.status === UserServiceContractStatusEnum.planning &&
      this.isMe()
    );
  }

  public get userService(): Partial<UserService> {
    return this.meeting.user_service ?? this.meeting?.user_service_contract?.user_service;
  }

  public meetingGroup(): string {
    return EDashboardMeetingGroup[this.meeting.has.meetingGroup];
  }

  public meetingCan(action: EMeetingUserAction | string): boolean {
    return this.meeting?.can?.[action] || false;
  }

  public meetingIs(state: EMeetingUserState | string): boolean {
    return this.meeting?.is?.[state] || false;
  }

  public contractCan(action: UserServiceContractActionEnum | string): boolean {
    return this.contract?.can?.[action] || false;
  }

  public contractIs(state: UserServiceContractStateEnum | string): boolean {
    return this.contract?.is?.[state] || false;
  }

  public redirectToJoin() {
    void this.router.navigate(this.meetingJoinRoute, { queryParams: { open: 'auto' } }).catch();
  }

  /** Принять приглашение */
  public acceptInvitation() {
    if (this.meetingCan('acceptInvitation')) {
      this.store.dispatch(acceptMeeting({ meeting_id: this.meeting.id }));
    }
  }

  /** Отменить приглашение кого-то на митинг */
  public withdrawInvitation() {
    alert('Not implemented');
    /* что такое "отказаться от приглашения на митинг" ? */

    // if (this.meetingCan('withdrawInvitation')) {
    //   if (this.meetingIs('single')) {
    //     // если это индивидуальный урок, то отменить его
    //     this.store.dispatch(requestMeetingCancel({ meeting_id: this.meeting.id }));
    //     this.startProcessing();
    //   } else {
    //     // если это групповой урок, то надо узнать - кому именно отменить приглашение
    //   }
    // }
  }

  /** Отказаться от приглашения */
  public declineInvitation() {
    if (this.meetingCan('declineInvitation')) {
      this.store.dispatch(declineMeeting({ meeting_id: this.meeting.id }));
      this.startProcessing();
    }
  }

  // Перенести время, отказаться от переноса, подтвердить перенос

  /** Отменить перенос времени */
  public withdrawReschedule() {
    if (this.meetingCan('withdrawReschedule')) {
      this.store.dispatch(withdrawMeetingReschedule({ meeting_id: this.meeting.id }));
      this.startProcessing();
    }
  }

  /** Принять приглашение на платный митинг */
  public paidAcceptInvitation() {
    // TODO if (!this.meetingCan(paidAcceptInvitation)) { return; }
    if (!this.hasCrossEvents) {
      this.startProcessing();
      this.meetingCheckoutDialogOpened = true;
      switch (this.participant.role) {
        case MeetingRoleEnum.reservation:
          this.acceptAndPayReason = 'dashboard.unconfirmed-causes.reservation';
          break;
        default:
          this.acceptAndPayReason = 'dashboard.unconfirmed-causes.received-invite-teacher';
          break;
      }
      /** @see onPaymentSucceed() */
    }
  }

  public acceptReschedule() {
    if (this.meetingCan('acceptReschedule')) {
      this.startProcessing();
      this.store.dispatch(acceptMeetingReschedule({ meeting_id: this.meeting.id }));
    }
    this.closeDeclineRescheduleAlert();
  }

  public openDeclineScheduleAlert() {
    this.declineRescheduleAlert = true;
    this.detectChanges();
  }

  public closeDeclineRescheduleAlert() {
    this.declineRescheduleAlert = false;
    this.detectChanges();
  }

  public rejectReschedule() {
    if (this.meetingCan('rejectReschedule')) {
      this.store.dispatch(rejectMeetingReschedule({ meeting_id: this.meeting.id }));
    }
    this.closeDeclineRescheduleAlert();
  }

  public openInviteModal() {
    this.state.inviteModal = true;
    this.markForCheck();
  }

  public openComplaintModal() {
    this.state.complaintForm = true;
    this.detectChanges();
  }

  public closeInviteModal() {
    this.state.inviteModal = false;
    this.markForCheck();
  }

  public leaveMeeting() {
    this.meetingsService
      .requestMeetingLeave(this._meeting.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response.can) {
            this.state.confirmationAction = 'leave';
            this.state.confirmationId = response.confirmation_id;
            this.state.confirmationOptions = response.confirmation;
            this.openConfirmationAlert();
          } else {
            logger.warn("Can't leave meeting for now");
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public cancelMeeting() {
    this.meetingsService
      .requestMeetingCancel(this._meeting.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response.can) {
            this.state.confirmationAction = 'cancel';
            this.state.confirmationId = response.confirmation_id;
            this.state.confirmationOptions = response.confirmation;
            this.openConfirmationAlert();
          } else {
            logger.warn("Can't cancel meeting for now");
          }
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public openConfirmationAlert() {
    this.state.confirmationAlert = true;
    this.detectChanges();
  }

  public closeConfirmationAlert() {
    this.state.confirmationAlert = false;
    this.detectChanges();
  }

  public confirm() {
    switch (this.state.confirmationAction) {
      case 'leave':
        return this.leaveMeetingConfirm();

      case 'cancel':
        return this.cancelMeetingConfirm();
    }
  }

  public leaveMeetingConfirm() {
    this.meetingsService
      .confirmMeetingLeave(this._meeting.id, this.state.confirmationId)
      .pipe(
        switchMap(() => this.meetingsBffService.getOne({ strategy: 'by-id', filter: { id: this.meeting.id } })),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (meeting) => {
          this._meeting = meeting;
          // this.setupCard();
          this.closeConfirmationAlert();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public cancelMeetingConfirm() {
    this.meetingsService
      .confirmMeetingCancel(this._meeting.id, this.state.confirmationId)
      .pipe(
        switchMap(() => this.meetingsBffService.getOne({ strategy: 'by-id', filter: { id: this.meeting.id } })),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (meeting) => {
          this._meeting = meeting;
          // this.setupCard();
          this.closeConfirmationAlert();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onReschedule() {
    this.store.dispatch(openMeetingRescheduleWindow({ meeting_id: this.meeting.id }));
  }

  public onCopied() {
    this.state.copied = true;
    this.markForCheck();

    this.setTimeout(() => {
      this.state.copied = false;
      this.markForCheck();
    }, 1000);
  }

  public isMeVersion(): boolean {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  public showMore(): void {
    void this.router.navigate(this.meetingJoinRoute).catch();
  }

  public onOpenChat() {
    const thread_id = this.meeting?.classroom_id || this.meeting?.classroom?.id;
    if (!thread_id) {
      captureMessage(`MeetingCardComponent: onOpenChat: no thread_id`, {
        extra: { meeting: this.meeting },
      });
      return;
    }
    this.chatService.openTreadChat(thread_id);
  }

  public dateStringToDate(date: string | Date): Date {
    const d = new Date(date);
    return formatDateByTimeZone(d, this.me?.timezone);
  }

  public openMeetingForm() {
    this.state.meetingFormModal = true;
    this.detectChanges();
  }

  public closeMeetingForm() {
    this.state.meetingFormModal = false;
    this.detectChanges();
  }

  public closeComplaintForm() {
    this.state.complaintForm = false;
    this.detectChanges();
  }

  public differenceInMinutes(): number {
    return differenceInMinutes(new Date(this.meeting?.end_at), new Date(this.meeting?.begin_at));
  }

  private initCard() {
    this.store
      .select(getSubjects)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (subjects) => {
          this.meetingSubject = subjects.find(
            (el) => el.id == (this.meeting.subject_id || this.meeting.subject?.id),
          )?.title;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
    this.meetingRoute = ContentRouter.meetingRoute(this.meeting) || [];
    this.meetingJoinRoute = ContentRouter.meetingJoinRoute(this.meeting);
    this.meetingUrl = this.request.host + this.router.createUrlTree(this.meetingJoinRoute).toString();
    this.participant =
      this.meeting && this.me ? (this.meeting.participants || []).find((p) => p.user_id === this.me.id) : null;

    if (this.meetingIs('rescheduling')) {
      this.rescheduleBeginDate = new Date(this.meeting.options?.reschedule_begin_at);
      this.rescheduleEndDate = new Date(this.meeting.options?.reschedule_end_at);
    }

    if (this.contract?.visit_info?.unpaid_order?.expire_at) {
      this.contractReservationExpireDate = new Date(this.contract?.visit_info?.unpaid_order?.expire_at);
    }
    if (this.meeting.has?.reservationExpireDate) {
      this.meetingReservationExpireDate = new Date(this.meeting.has.reservationExpireDate);
    }

    this.cover = {
      type: 'cover',
      img: this.meetingsService.getCoverUrl(this.meeting),
      title: this.meetingsService.meetingFullTitle(this.meeting),
      aspect: '16/9',
      form: 'base',
    };
    this.detectChanges();

    if (this.meetingIs('owner')) {
      this.loadStudent$(this.firstStudent?.user_id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => this.detectChanges(),
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    } else {
      this.loadAuthor$(this._meeting.author_id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => this.detectChanges(),
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    }
  }

  public get showMeetingFooter(): boolean {
    return (
      this.isMeetingReservationCountdown ||
      this.meetingIs('rescheduling') ||
      this.meetingCan('withdrawInvitation') ||
      this.meetingCan('declineInvitation') ||
      this.meetingCan('paidAcceptInvitation') ||
      this.meetingCan('acceptInvitation') ||
      this.meetingCan('rejectReschedule') ||
      this.meetingCan('acceptReschedule') ||
      this.meetingCan('withdrawReschedule') ||
      this.meetingCan('withdrawReschedule') ||
      this.paidInProcess
    );
  }

  public get showContractFooter(): boolean {
    return this.isContractReservationCountdown || this.contract?.is?.accepting || this.contract?.is?.suspended;
  }

  public get isSingleMeeting(): boolean {
    return this.meeting.type === MeetingTypeEnum.single;
  }

  public get isGroupMeeting() {
    return !this.isSingleMeeting;
  }

  private startProcessing() {
    this.processing = true;
    this.detectChanges();
  }

  private stopProcessing() {
    this.processing = false;
    this.detectChanges();
  }

  public closeMeetingCheckoutDialog() {
    this.meetingCheckoutDialogOpened = false;
    this.stopProcessing();
    this.detectChanges();
  }

  /** Успешная оплата платного участия */
  public onPaymentSucceed() {
    this.meetingCheckoutDialogOpened = false;
    this.startProcessing();
    this.acceptMeeting();
  }

  public onContractPaymentSucceed() {
    this.serviceCheckoutDialogOpened = false;
    this.startProcessing();
    this.acceptMeeting();
  }

  public get isMeetingReservationCountdown(): boolean {
    return (
      !!this.meetingReservationExpireDate && differenceInSeconds(this.meetingReservationExpireDate, new Date()) > 0
    );
  }

  public get isContractReservationCountdown(): boolean {
    return (
      !!this.contractReservationExpireDate && differenceInSeconds(this.contractReservationExpireDate, new Date()) > 0
    );
  }

  public get metingType(): string | false {
    if (!this.userService?.type) return false;

    switch (this.userService?.type) {
      case EUserServiceType.single:
        return 'single';
      case EUserServiceType.regular:
        return 'regular';
      case EUserServiceType.mini:
        return 'mini';
      case EUserServiceType.course:
        return 'course';
    }
  }

  public classes(): string {
    switch (this.userService?.type) {
      case EUserServiceType.single:
        return '1';
      case EUserServiceType.regular:
        return 'event-calendar.ongoing';
      case EUserServiceType.mini:
      case EUserServiceType.course:
        return `${this.meeting?.options?.sequence_number || 0}/${this.meeting?.options?.sequence_total || 0}`;
    }
  }

  /** Заголовок области подтверждения для отдельного митинга */
  public get meetingActionTitle(): string {
    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      this.meeting.has.meetingGroup === EDashboardMeetingGroup.unconfirmed ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      this.meeting.has.meetingGroup === EDashboardMeetingGroup.upcoming
    ) {
      // return 'Нужно что-то подтвердить';
      if (this.meetingIs('rescheduling')) {
        if (this.meetingCan('rejectReschedule')) {
          return 'dashboard.reschedule_required';
        } else {
          return 'dashboard.reschedule_requested';
        }
      }

      if (this.meeting.has.reservationExpireDate) {
        if (this.meetingCan('paidJoin')) {
          return 'dashboard.meeting_reservation_unpaid';
        } else {
          return 'dashboard.meeting_reservation_waiting';
        }
      }

      if (this.meetingCan('acceptInvitation')) {
        return 'dashboard.accept_required';
      }

      if (this.meetingCan('paidAcceptInvitation')) {
        return 'dashboard.paid_accept_required_simple';
      }

      if (this.meetingCan('paidJoin')) {
        return 'dashboard.paid_join_required';
      }
    }

    return '';
  }

  /** Заголовок области подтверждения для митинга в составе контракта */
  public get contractActionTitle(): string {
    if (this.userService?.is?.individual) {
      // индивидуальное

      if (this.userService?.is?.onetime) {
        // индивидуальное разовое
        if (this.contract?.is?.accepting) {
          return 'dashboard.meeting_reservation_unpaid';
        }
      }

      if (this.userService?.is?.ongoing) {
        // индивидуальное регулярное

        if (this.contract?.is?.accepting) {
          // первая оплата
          return 'dashboard.meeting_reservation_waiting';
        } else if (this.contract?.is?.suspended) {
          // последующие оплаты
          return 'meetings.subscription_suspended';
        }
      }

      if (this.userService?.is?.series) {
        // индивидуальный курс/миникурс
        if (this.contract?.is?.accepting) {
          // первая оплата
          return 'dashboard.course_reservation_unpaid';
        }
      }
    }

    if (this.userService?.is?.group) {
      // групповое

      if (this.userService?.is?.onetime) {
        // групповое разовое
        if (this.contract?.is?.accepting) {
          return 'dashboard.meeting_reservation_unpaid';
        }
      }

      if (this.userService?.is?.ongoing) {
        // групповое регулярное
        if (this.contract?.is?.accepting) {
          // первая оплата
          return 'dashboard.meeting_reservation_waiting';
        } else if (this.contract?.is?.suspended) {
          // последующие оплаты
          return 'meetings.subscription_suspended';
        }
      }

      if (this.userService?.is?.series) {
        // групповой курс/миникурс
        if (this.contract?.is?.accepting) {
          // первая оплата
          return 'dashboard.course_reservation_unpaid';
        }
      }
    }

    return '';
  }

  public get contractSetupPaymentsTitle(): string {
    if (this.userService?.is?.individual || this.userService?.is?.group) {
      // индивидуальное

      if (this.userService?.is?.onetime) {
        // индивидуальное разовое
        if (this.contract?.is?.accepting) {
          return 'dashboard.label-btn.accept-and-pay';
        }
      }

      if (this.userService?.is?.ongoing) {
        // индивидуальное регулярное

        if (this.contract?.is?.accepting) {
          // первая оплата
          return 'dashboard.label-btn.accept-and-pay';
        } else if (this.contract?.is?.suspended) {
          // последующие оплаты
          return 'meetings.renew_subscription';
        }
      }

      if (this.userService?.is?.series) {
        // индивидуальный курс/миникурс
        if (this.contract?.is?.accepting) {
          // первая оплата
          return 'dashboard.label-btn.accept-and-pay';
        }
      }
    }

    return 'Надо что-то оплатить, но что именно?';
  }

  public openContractPaymentsSetupForm() {
    this.serviceCheckoutDialogOpened = true;
    this.detectChanges();
  }

  public closeServiceCheckoutDialog() {
    this.serviceCheckoutDialogOpened = false;
    this.detectChanges();
  }

  private acceptMeeting() {
    if (!this.meeting && !this.meeting.is && this.meeting.is.acceptedParticipant) {
      // приглашение принято
      // logger.log('EventCardComponent:acceptMeeting', 'is already accepted');
      return;
    }

    // Повторять принятие приглашения до тех пор, пока не будет принято
    // (повторы нужны для того, чтобы дождаться асинхронной обработки оплаты)
    // TODO заменить повторы на веб-сокеты с уведомлением о том, что митинг или участник митинга обновлён

    this.setInterval(() => {
      if (this.acceptMeetingIntervalLock) {
        return;
      }
      this.acceptMeetingIntervalLock = true;
      this.meetingsService
        .acceptMeeting(this.meeting.id)
        .pipe(first(), takeUntil(this.destroyed$))
        .subscribe({
          next: ([meeting, participant]) => {
            this.acceptMeetingIntervalLock = false;

            this.participant = participant;

            if (meeting.is.acceptedParticipant) {
              // приглашение принято
              logger.log('EventCardComponent:acceptMeeting', 'now accepted');
              // TODO success info

              this.stopProcessing();
            }
          },
          error: (err: any) => {
            logger.log(err);
            this.acceptMeetingIntervalLock = false;
          },
        });
    }, 5758);
  }

  public onTimeOver() {
    this.setTimeout(() => {
      this.changed.emit();
      this.detectChanges();
    }, 5000);
  }

  public openBreakAlert() {
    this.classBreakReasons = 'no_classes';

    if (this.meeting.user_service) {
      if (this.meeting.user_service.type === EUserServiceType.regular) {
        this.alertData = classBreakReasonsData['regular'][this.classBreakReasons];
      } else if (
        this.meeting.user_service.type === EUserServiceType.course ||
        this.meeting.user_service.type === EUserServiceType.mini
      ) {
        this.alertData = classBreakReasonsData['course'][this.classBreakReasons];
      } else {
        this.alertData = null;
      }
    } else {
      this.alertData = null;
    }

    this.classBreakAlertShow = true;
    this.detectChanges();
  }

  public breakContract() {
    if (!this.meeting.user_service_contract_id) {
      return;
    }

    const request = new BreakUserServiceContractRequest({
      user_service_contract_id: this.meeting.user_service_contract_id,
      reason: null, // TODO
      comment: null, // TODO
    });
    this.contractsService
      .breakContract(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this.changed.emit(!!res);
          this.classBreakAlertShow = false;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private get firstStudent() {
    return (this.meeting?.participants || []).find((p) =>
      [MeetingRoleEnum.participant, MeetingRoleEnum.reservation].includes(p.role),
    );
  }

  private loadAuthor$(id: string) {
    if (!id) {
      return of(null);
    }
    return this.accountsService.getUserById(id, 'LOAD_TIME').pipe(
      tap((user) => {
        this.author = user;
        this.avatar = {
          img: FilesService.getFileUrlBySize(this.author?.userpic_id, ImageSizeEnum.sm),
          form: 'circle',
          type: 'avatar',
        };
      }),
    );
  }

  private loadStudent$(id) {
    if (!id) {
      return of(null);
    }
    return this.accountsService.getUserById(id, 'LOAD_TIME').pipe(
      tap((user) => {
        this.student = user;
        this.avatar = {
          img: FilesService.getFileUrlBySize(this.student?.userpic_id, ImageSizeEnum.sm),
          form: 'circle',
          type: 'avatar',
        };
      }),
    );
  }
}
